<script setup lang="ts">
/**
 * HALAMAN PRODUK — BerkasKilat, tema v3. Contoh penerapan gaya baru pada
 * halaman yang meminta uang.
 *
 * Halaman ini statis: tidak memanggil backend, tidak ada useFetch. Semua
 * "keadaan" yang ditampilkan adalah contoh tampilan, dan diberi label sebagai
 * ilustrasi supaya tidak terbaca sebagai janji hasil.
 */


const langkah = [
  {
    ikon: 'i-lucide-credit-card',
    judul: '1. Bayar Rp15.000 lewat QRIS',
    detail: 'Pindai dari e-wallet atau m-banking mana pun. Tanpa akun, tanpa nomor kartu.'
  },
  {
    ikon: 'i-lucide-upload',
    judul: '2. Unggah semua berkas Anda',
    detail: 'Pas foto, KTP, ijazah, transkrip — sekaligus. Pilih instansi tujuan Anda.'
  },
  {
    ikon: 'i-lucide-download',
    judul: '3. Unduh hasil yang sudah sesuai',
    detail: 'Ukuran dan formatnya sudah mengikuti ketentuan. Boleh diulang selama 3 hari.'
  }
]

const jenisBerkas = [
  { ikon: 'i-lucide-user-square', nama: 'Pas foto', detail: 'Ukuran cetak dan latar sesuai ketentuan' },
  { ikon: 'i-lucide-credit-card', nama: 'KTP & KK', detail: 'Hasil scan diperkecil tanpa jadi buram' },
  { ikon: 'i-lucide-scroll-text', nama: 'Ijazah', detail: 'PDF besar dipadatkan sampai lolos unggah' },
  { ikon: 'i-lucide-table-2', nama: 'Transkrip nilai', detail: 'Beberapa halaman digabung rapi' },
  { ikon: 'i-lucide-file-signature', nama: 'Surat pernyataan', detail: 'Tanda tangan tetap terbaca jelas' },
  { ikon: 'i-lucide-file-plus', nama: 'Berkas lain', detail: 'Dokumen pendukung apa pun yang diminta' }
]

const faq = [
  {
    label: 'Instansi apa saja yang didukung?',
    content:
      'Anda memilih target ukuran dan format sendiri sesuai pengumuman resmi instansi tujuan. Ketentuan tiap instansi berbeda dan bisa berubah sewaktu-waktu, jadi selalu periksa ulang pengumuman resminya sebelum mengunggah.'
  },
  {
    label: 'Akses 3 hari itu maksudnya bagaimana?',
    content:
      'Terhitung sejak kode aktivasi Anda pakai pertama kali, Anda boleh memproses berkas berkali-kali selama tiga hari. Berguna karena pengumuman instansi kadang direvisi di tengah masa pendaftaran.'
  },
  {
    label: 'Berkas saya aman?',
    content:
      'Berkas yang Anda unggah dipakai hanya untuk pemrosesan dan dihapus otomatis setelah selesai. Kami tidak membagikannya kepada siapa pun.'
  },
  {
    label: 'Kalau hasilnya tidak sesuai?',
    content:
      'Selama masa akses tiga hari Anda bisa mengulang prosesnya tanpa biaya tambahan. Bila tetap bermasalah, hubungi kami dengan menyertakan ketentuan resmi yang Anda pakai.'
  }
]

const { adaKontak, adaWa, tautanWa } = useIdentitas()

/* Sinyal kepercayaan yang bisa dipertanggungjawabkan tanpa data tambahan.
   Tidak menyebut angka masa simpan yang belum dikonfirmasi pemilik. */
const sinyalPercaya = computed(() => [
  { ikon: 'i-lucide-qr-code', judul: 'Bayar via QRIS', detail: 'Semua e-wallet & m-banking' },
  { ikon: 'i-lucide-user-x', judul: 'Tanpa daftar akun', detail: 'Cukup kode aktivasi' },
  { ikon: 'i-lucide-shield-check', judul: 'Berkas dihapus otomatis', detail: 'Setelah proses selesai' },
  ...(adaKontak
    ? [{ ikon: 'i-lucide-message-circle', judul: 'Ada bantuan manusia', detail: 'Kalau kode tidak keluar' }]
    : [{ ikon: 'i-lucide-wallet', judul: 'Tanpa langganan', detail: 'Sekali bayar, selesai' }])
])

/* SEO dibawa UTUH dari versi lama halaman ini (kini di /legacy/berkaskilat).
   Ini halaman yang meminta uang: kehilangan canonical, Open Graph, dan
   SoftwareApplication+Offer di sini berarti kehilangan tampilan kaya di hasil
   pencarian dan pratinjau saat tautannya dibagikan di WhatsApp. */
const situs = useSitusUrl()

pakaiSeo({
  judul: 'BerkasKilat — semua berkas pendaftaran beres sekali jalan',
  deskripsi:
    'Unggah semua berkas pendaftaran sekaligus, pilih preset instansi, terima ZIP siap unggah plus laporan per berkas. Rp15.000 untuk akses 3 hari tanpa batas proses.',
  path: '/berkaskilat',
  jsonLd: [
    jsonLdAplikasi({
      nama: 'BerkasKilat',
      deskripsi:
        'Layanan penyiapan berkas pendaftaran: menyesuaikan format, ukuran, dan penamaan seluruh dokumen dengan ketentuan resmi instansi dalam satu kali proses.',
      url: situs + '/berkaskilat',
      harga: 15000
    }),
    jsonLdFaq(faq.map((f) => ({ tanya: f.label, jawab: f.content }))),
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['BerkasKilat', '/berkaskilat']
    ])
  ]
})
</script>

<template>
  <div class="pb-24 lg:pb-0">
    <!-- HERO PRODUK -->
    <section class="relative overflow-hidden">
      <!-- Aura hanyut halus saat digulir (desktop; di HP diam, nol biaya). -->
      <div class="ca-aura" data-parallax="0.05" aria-hidden="true" />

      <div class="relative mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 pt-10 sm:pt-16 pb-12">
        <!-- `isolate`: kartu harga di dalam grid ini `lg:sticky` — elemen
             berposisi (sticky/relative/absolute) SELALU dicat di atas
             elemen statis, TERLEPAS dari urutan DOM. Tanpa isolasi ini,
             kartu bisa menang lapisan atas <CaTrustBar> yang datang
             SETELAH grid ini (saudara, bukan anak), menutupinya begitu
             area stickinya aktif. `isolate` membuat grid ini SATU unit
             tumpukan terhadap saudaranya sendiri — stacking internalnya
             (termasuk sticky) tidak bisa lagi bocor ke luar batas grid. -->
        <div class="isolate grid gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-14 lg:items-start">
          <div>
            <CaBadge tone="emas" icon="i-lucide-folder-check">BerkasKilat</CaBadge>

            <h1 class="ca-display mt-5 text-ink text-balance">
              Semua berkas pendaftaran, beres sekali jalan
            </h1>

            <p class="mt-5 text-lg sm:text-xl text-muted leading-relaxed max-w-xl">
              Pas foto, KTP, ijazah, transkrip — unggah sekaligus, kami sesuaikan ukuran dan
              formatnya dengan ketentuan instansi tujuan Anda.
            </p>

            <div class="mt-8 hidden lg:flex flex-col sm:flex-row gap-3">
              <CaButton variant="emas" size="lg" to="/checkout?sku=bk-3d">
                Beresin berkas saya — Rp15.000
              </CaButton>
              <CaButton variant="secondary" size="lg" to="#cara-kerja">
                Lihat cara kerjanya
              </CaButton>
            </div>

            <p class="ca-caption text-muted mt-4">
              Sudah punya kode?
              <NuxtLink to="/redeem" class="text-brand font-semibold underline underline-offset-2">
                Tukar di sini
              </NuxtLink>
            </p>
          </div>

          <!-- Kartu harga ringkas, menempel di desktop supaya CTA selalu terlihat. -->
          <CaCard tone="emas" class="lg:sticky lg:top-24 ca-pita-emas relative overflow-hidden">
            <p class="ca-caption font-semibold uppercase tracking-wide text-muted">Sekali bayar</p>
            <p class="ca-price mt-2 text-ink">
              Rp15.000<span class="ml-1.5 text-sm font-medium text-muted tracking-normal">
                / akses 3 hari
              </span>
            </p>

            <ul class="mt-6 flex flex-col gap-2.5">
              <li
                v-for="f in [
                  'Semua jenis berkas, sekali unggah',
                  'Boleh diulang selama 3 hari',
                  'Berkas dihapus otomatis',
                  'Tanpa langganan, tanpa akun'
                ]"
                :key="f"
                class="flex gap-2.5 text-[0.9375rem] leading-relaxed text-ink"
              >
                <UIcon name="i-lucide-check" class="size-5 shrink-0 text-ok mt-0.5" aria-hidden="true" />
                <span>{{ f }}</span>
              </li>
            </ul>

            <CaButton variant="emas" size="lg" block to="/checkout?sku=bk-3d" class="mt-7">
              Bayar dengan QRIS
            </CaButton>
            <p class="ca-caption text-muted mt-3 text-center">
              Kode aktivasi keluar begitu pembayaran masuk.
            </p>
          </CaCard>
        </div>

        <div class="mt-12">
          <CaTrustBar ringkas :butir="sinyalPercaya" />
        </div>
      </div>
    </section>

    <!-- JENIS BERKAS -->
    <section class="py-14 sm:py-20 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Satu alat untuk semuanya"
          badge-ikon="i-lucide-layers"
          judul="Berkas apa saja yang bisa dirapikan"
          ringkas="Tidak perlu membuka empat aplikasi berbeda untuk empat jenis berkas."
        />

        <!-- Reveal di wadah luar; tilt 3D halus di tiap kartu dalam satu grup.
             Dipisah supaya `transform` reveal & tilt tak berebut elemen sama.
             Ubin ikon diberi .ca-lapis-3d agar berkedalaman juga di HP. -->
        <div data-muncul class="mt-9">
          <div data-tilt-grup class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CaCard
              v-for="b in jenisBerkas"
              :key="b.nama"
              data-tilt
              interaktif
              class="flex items-start gap-3.5"
            >
              <span class="ca-ikon-emas ca-lapis-3d grid place-items-center size-10 shrink-0 rounded-control" aria-hidden="true">
                <UIcon :name="b.ikon" class="size-5" />
              </span>
              <div class="min-w-0">
                <p class="font-bold text-ink leading-snug">{{ b.nama }}</p>
                <p class="ca-caption text-muted mt-1 leading-relaxed">{{ b.detail }}</p>
              </div>
            </CaCard>
          </div>
        </div>
      </div>
    </section>

    <!-- CARA KERJA -->
    <section id="cara-kerja" class="ca-tunda scroll-mt-20 py-14 sm:py-20">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Tiga langkah"
          badge-ikon="i-lucide-route"
          judul="Dari bayar sampai berkas siap unggah"
        />
        <div class="mt-9">
          <CaLangkah :langkah="langkah" runtun />
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="ca-tunda py-14 sm:py-20 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          badge="Sebelum membeli"
          badge-ikon="i-lucide-message-circle-question"
          judul="Pertanyaan yang sering masuk"
        />
        <div class="mt-8 max-w-3xl">
          <CaFaqAccordion :item="faq" />
        </div>

        <div v-if="adaKontak && adaWa" class="mt-8">
          <CaButton
            variant="secondary"
            size="lg"
            icon="i-lucide-message-circle"
            :href="tautanWa({ konteks: 'BerkasKilat' })"
          >Tanya dulu lewat WhatsApp</CaButton>
        </div>
      </div>
    </section>

    <!-- PENUTUP -->
    <section class="ca-tunda py-14 sm:py-20">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <CaSectionHeading
          tengah
          judul="Deadline tidak menunggu berkas Anda"
          ringkas="Rp15.000 untuk akses tiga hari penuh. Tanpa langganan, tanpa akun."
        />
        <div class="mt-7 flex flex-col sm:flex-row gap-3 sm:justify-center">
          <CaButton variant="emas" size="lg" to="/checkout?sku=bk-3d">Beresin berkas saya</CaButton>
          <CaButton variant="secondary" size="lg" to="/#alat">Lihat alat lainnya</CaButton>
        </div>
      </div>
    </section>

    <!-- Bilah CTA bawah — HP saja. Instans kaca kedua (dan terakhir) di halaman
         ini; header adalah yang pertama. Lebih dari dua = frame drop di GPU
         kelas bawah. -->
    <CaGlassBar posisi="bawah" class="lg:hidden">
      <div class="flex items-center gap-3 px-4 py-3">
        <div class="min-w-0 flex-1">
          <p class="ca-num text-lg font-extrabold leading-none text-ink">Rp15.000</p>
          <p class="ca-caption text-muted mt-1 truncate">Akses 3 hari</p>
        </div>
        <CaButton variant="emas" size="lg" to="/checkout?sku=bk-3d" class="shrink-0">Bayar QRIS</CaButton>
      </div>
    </CaGlassBar>
  </div>
</template>
